import type { UseQueryResult } from '@tanstack/react-query';
import type { GetAggregatedFormDataParams, GetFormTrendsResponse } from '#services/api/insights.ts';
import type { Metric } from './FormTrends.types.ts';

import * as React from 'react';
import { Skeleton } from '@mui/material';
import { useQueries } from '@tanstack/react-query';

import { formTrends } from '#lib/react-query/queries.ts';
import { CHART_X_AXIS_CONFIG, WIDGET_CHART_HEIGHT } from '#utils/charts.ts';
import { EmptyDataOverlay } from '../EmptyDataOverlay.tsx';
import { ViewChart } from './ViewChart.tsx';

interface MappedItem extends Omit<GetFormTrendsResponse['data'][number], 'period'> {
  period: number;
}

function select(response: GetFormTrendsResponse) {
  const data: Record<number, MappedItem> = {};

  for (const item of response.data) {
    const period = new Date(item.period).getTime();
    data[period] = { ...item, period };
  }

  return { ...response, data };
}

export function ComparisonView(props: { metric: Metric; params: GetAggregatedFormDataParams }) {
  const combine = React.useCallback(
    (results: UseQueryResult<ReturnType<typeof select>>[]) => {
      if (results.some((result) => result.isLoading)) {
        return { loading: true, data: [], groupedBy: 'day' as const };
      }

      if (results.some((result) => !result.data)) {
        return { loading: false, data: [], groupedBy: 'day' as const };
      }

      const periods = new Set<number>();

      for (const result of results) {
        for (const period of Object.keys(result.data!.data)) {
          periods.add(Number(period));
        }
      }

      const sortedPeriods = [...periods].sort((a, b) => a - b);
      const data: { required: number; optional: number; period: number }[] = [];

      for (const sortedPeriod of sortedPeriods) {
        data.push({
          // Results order is guaranteed to be the same as the queries order
          required: results[0]!.data!.data[sortedPeriod]?.[props.metric] ?? 0,
          optional: results[1]!.data!.data[sortedPeriod]?.[props.metric] ?? 0,
          period: sortedPeriod,
        });
      }

      return { loading: false, data, groupedBy: results[0]!.data!.groupedBy };
    },
    [props.metric]
  );

  const queries = useQueries({
    queries: [
      {
        ...formTrends({ ...props.params, assessmentTypeRequired: 1 }),
        select,
      },
      {
        ...formTrends({ ...props.params, assessmentTypeRequired: 0 }),
        select,
      },
    ],
    combine,
  });

  if (queries.loading) {
    return <Skeleton variant="rounded" height={WIDGET_CHART_HEIGHT} />;
  }

  if (queries.data.length === 0) {
    return <EmptyDataOverlay />;
  }

  return (
    <ViewChart
      dataset={queries.data}
      series={[
        {
          dataKey: 'required',
          label: 'Required',
          labelMarkType: 'circle',
          type: 'line',
        },
        {
          dataKey: 'optional',
          label: 'Optional',
          labelMarkType: 'circle',
          type: 'line',
        },
      ]}
      xAxis={[
        {
          dataKey: 'period',
          height: 60,
          label: 'Dates',
          scaleType: 'time',
          ...CHART_X_AXIS_CONFIG[queries.groupedBy],
        },
      ]}
    />
  );
}
